import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

type Template = {
  id: string;
  label: string;
  message: string;
  defaultUpdated: string;
};

// Admin-only content. To edit templates, update this array in code.
const TEMPLATES: Template[] = [
  {
    id: "pm-internship",
    label: "PM Internship Scheme",
    message:
      "Looking for internships? Check the official PM Internship Scheme here: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "pmkvy",
    label: "PMKVY (Skill India)",
    message:
      "Free short-term skill training with certification under PMKVY. Details here: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "naps",
    label: "NAPS Apprenticeship",
    message:
      "Earn while you learn through NAPS apprenticeships (stipend + certificate). Apply here: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "nptel",
    label: "NPTEL Free Courses",
    message:
      "Free IIT/IISc courses on NPTEL with optional certification. Browse here: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "pragati",
    label: "AICTE Pragati Scholarship (Girls)",
    message:
      "Pragati Scholarship for girl students in technical education. Check eligibility here: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "nsp",
    label: "National Scholarship Portal",
    message:
      "Central & state scholarships in one place on the National Scholarship Portal: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "swayam",
    label: "SWAYAM Free Courses",
    message:
      "Free online courses from top Indian universities on SWAYAM: [link]",
    defaultUpdated: "2026-07-23",
  },
  {
    id: "scam-alert",
    label: "Scam Alert",
    message:
      "Never pay fees for a government scheme or job. Verify every offer on the official site: [link]",
    defaultUpdated: "2026-07-23",
  },
];

const STORAGE_KEY = "wh_toolkit_updated_v1";

const AmbassadorToolkit = () => {
  const { user, loading, isAdmin } = useAuth();
  const [updated, setUpdated] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setUpdated(JSON.parse(raw));
    } catch {}
  }, []);

  const setDate = (id: string, value: string) => {
    setUpdated((prev) => {
      const next = { ...prev, [id]: value };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const copy = async (t: Template) => {
    try {
      await navigator.clipboard.writeText(t.message);
      setCopiedId(t.id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedId((c) => (c === t.id ? null : c)), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) return <div className="p-8 text-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-16 text-center">
          <h1 className="text-2xl font-bold mb-2">Ambassadors only</h1>
          <p className="text-muted-foreground">
            You need ambassador/admin access to view the toolkit.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8 max-w-3xl">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold">Ambassador Toolkit</h1>
          <p className="text-muted-foreground mt-1">
            Ready-to-share WhatsApp templates. Replace <code>[link]</code> with the official URL before sending.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            <Link to="/ambassador/mentor" className="text-primary hover:underline">
              ← Back to Mentor Dashboard
            </Link>
          </p>
        </div>

        <ul className="divide-y divide-border border border-border rounded-xl bg-card">
          {TEMPLATES.map((t) => {
            const date = updated[t.id] ?? t.defaultUpdated;
            return (
              <li key={t.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold">{t.label}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copy(t)}
                    aria-label={`Copy ${t.label} message`}
                  >
                    {copiedId === t.id ? (
                      <Check className="h-4 w-4 mr-1" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1" />
                    )}
                    Copy
                  </Button>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">{t.message}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <label htmlFor={`date-${t.id}`}>Last updated:</label>
                  <Input
                    id={`date-${t.id}`}
                    type="date"
                    value={date}
                    onChange={(e) => setDate(t.id, e.target.value)}
                    className="h-8 w-40 text-xs"
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </main>
      <Footer />
    </div>
  );
};

export default AmbassadorToolkit;